import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { PLATFORM_FEATURES } from "@/lib/platform-features";
import { Button } from "@/components/ui/button";
import { Lock, ArrowUpCircle, ArrowLeft } from "lucide-react";

interface FeatureGuardProps {
  featureKey: string;
  children: React.ReactNode;
}

const FeatureGuard = ({ featureKey, children }: FeatureGuardProps) => {
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const planLimits = usePlanLimits(restaurantId);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }
      setAuthenticated(true);

      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      if (impersonateId) {
        setRestaurantId(impersonateId);
      } else {
        const { data: rest } = await supabase
          .from("restaurants")
          .select("id")
          .eq("owner_id", user.id)
          .maybeSingle();
        setRestaurantId(rest?.id || null);
      }
      setLoading(false);
    };
    load();
  }, [navigate]);

  // Still loading auth or plan data
  if (loading || !authenticated || planLimits.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Feature is allowed
  if (planLimits.hasFeature(featureKey)) {
    return <>{children}</>;
  }

  // Feature is NOT allowed — show upgrade screen
  const feature = PLATFORM_FEATURES.find(f => f.key === featureKey);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center">
          <Lock className="w-10 h-10 text-muted-foreground" />
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Recurso não disponível
          </h1>
          <p className="text-muted-foreground">
            {feature ? (
              <>
                <span className="font-semibold text-foreground">{feature.label}</span> não está incluído no seu plano atual
                {planLimits.planName !== "Sem plano" && (
                  <> (<span className="font-medium">{planLimits.planName}</span>)</>
                )}.
              </>
            ) : (
              <>Este recurso não está incluído no seu plano atual.</>
            )}
          </p>
          {feature && (
            <p className="text-sm text-muted-foreground mt-1">{feature.description}</p>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => navigate("/dashboard?upgrade=true")}
            className="w-full gap-2"
            size="lg"
          >
            <ArrowUpCircle className="w-5 h-5" />
            Fazer Upgrade do Plano
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="w-full gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeatureGuard;
